if (!customElements.get('refill-packs')) {
  customElements.define(
    'refill-packs',
    class RefillPacks extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) return;

        this.originalPriceHtml = this.priceElement?.innerHTML ?? null;

        this.addEventListener('click', this.onPackClick);
        this.form.addEventListener('formdata', this.onFormData);

        // Full prices first, then the tier prices once Tek has loaded its rules.
        this.update();
        window.TekVolumePricing?.whenReady(this.update);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onPackClick);
        this.form?.removeEventListener('formdata', this.onFormData);
      }

      get priceElement() {
        return document.getElementById(this.dataset.priceId);
      }

      get packButtons() {
        return Array.from(this.querySelectorAll('.dtc-volume-pricing__button'));
      }

      get selectedRefills() {
        const button = this.querySelector('.dtc-volume-pricing__button.is-selected');
        return Number.parseInt(button?.dataset.refills, 10) || 0;
      }

      // Selection is marked the way the DTC widget marks its tiers: is-selected on the button and its item.
      onPackClick = (event) => {
        const selectedButton = event.target.closest('.dtc-volume-pricing__button');
        if (!selectedButton || selectedButton.disabled) return;

        event.preventDefault();

        this.packButtons.forEach((button) => {
          const isSelected = button === selectedButton;
          button.classList.toggle('is-selected', isSelected);
          button.parentElement?.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));
        });

        this.update();
      };

      // ProductForm builds its request from `new FormData(form)`, which fires this event, so the
      // packs decide what is added before anything is sent. The quantity selectors are hidden while
      // the packs show, so their value, possibly left over from the refill, is dropped: the hose-end
      // alone adds one, and a pack swaps the single line for one hose-end and its refills, since
      // /cart/add takes `items[n][...]` fields in place of `id` and `quantity`.
      onFormData = (event) => {
        const { formData } = event;
        const refills = this.selectedRefills;

        formData.delete('quantity');

        if (!refills) {
          formData.append('quantity', 1);
          return;
        }

        formData.delete('id');
        formData.append('items[0][id]', this.dataset.hoseEndId);
        formData.append('items[0][quantity]', 1);
        // Marks the hose-end as part of a pack, so the cart shows its quantity without the +/- buttons.
        // The leading underscore keeps it out of the cart and checkout line details.
        formData.append('items[0][properties][_refill_pack]', 1);
        formData.append('items[1][id]', this.dataset.refillId);
        formData.append('items[1][quantity]', refills);
      };

      update = () => {
        // A variant change swaps this element out while Tek may still be loading; the stale copy must
        // not write its prices over the new one's.
        if (!this.isConnected) return;

        const discountGroups = this.getDiscountGroups();

        this.packButtons.forEach((button) => {
          const refills = Number.parseInt(button.dataset.refills, 10) || 0;
          const { price, compareAtPrice } = this.getPackPrices(refills, 1, discountGroups);
          const priceElement = button.querySelector('.refill-pack__price');
          const labelElement = button.parentElement?.querySelector('.dtc-volume-pricing__label');

          if (priceElement) priceElement.textContent = this.formatMoney(price);
          if (labelElement) labelElement.textContent = this.getSavingText(price, compareAtPrice);
        });

        const refills = this.selectedRefills;
        const packPrices = this.getPackPrices(refills, 1, discountGroups);

        this.updatePrice(refills ? packPrices : null);
        this.updateSubmitPrice(packPrices.price);
      };

      // The hose-end and the refill are each priced by the Tek group that covers them. A group counting
      // per variant tiers each line on its own quantity, so a pack's single hose-end stays at full
      // price; one counting per product tiers both lines on the pack's total, so 1 + 1 reaches 2+.
      getDiscountGroups() {
        const tek = window.TekVolumePricing;

        return {
          hoseEnd: tek?.findVariantGroup(this.dataset.hoseEndId) || null,
          refill: tek?.findVariantGroup(this.dataset.refillId) || null,
        };
      }

      // The quantity each line reaches its tier with.
      getTierQuantities(hoseEndQuantity, refillQuantity, discountGroups) {
        const { hoseEnd, refill } = discountGroups;
        if (hoseEnd && hoseEnd === refill && window.TekVolumePricing.countsPerProduct(hoseEnd)) {
          const total = hoseEndQuantity + refillQuantity;
          return { hoseEnd: total, refill: total };
        }

        return { hoseEnd: hoseEndQuantity, refill: refillQuantity };
      }

      getLinePrice(price, quantity, tierQuantity, discountGroup) {
        const unitPrice = discountGroup
          ? window.TekVolumePricing.getUnitPrice(discountGroup, price, tierQuantity)
          : price;
        return unitPrice * quantity;
      }

      getPackPrices(refills, quantity, discountGroups) {
        const refillQuantity = refills * quantity;
        const tierQuantities = this.getTierQuantities(quantity, refillQuantity, discountGroups);
        const price =
          this.getLinePrice(Number(this.dataset.hoseEndPrice), quantity, tierQuantities.hoseEnd, discountGroups.hoseEnd) +
          this.getLinePrice(Number(this.dataset.refillPrice), refillQuantity, tierQuantities.refill, discountGroups.refill);
        const compareAtPrice =
          Number(this.dataset.hoseEndCompareAtPrice) * quantity +
          Number(this.dataset.refillCompareAtPrice) * refillQuantity;

        return { price, compareAtPrice };
      }

      getSavingText(price, compareAtPrice) {
        if (!(compareAtPrice > price)) return '';
        return `Save ${Math.round(((compareAtPrice - price) / compareAtPrice) * 100)}%`;
      }

      formatMoney(cents) {
        return window.FoxTheme?.Currency?.formatMoney
          ? window.FoxTheme.Currency.formatMoney(cents, this.dataset.moneyFormat)
          : '';
      }

      // Mirrors snippets/price.liquid: f-price--on-sale decides whether the regular or sale block
      // shows. The hose-end alone puts back the price the section rendered.
      updatePrice(prices) {
        const priceElement = this.priceElement;
        if (!priceElement) return;

        if (!prices) {
          if (this.originalPriceHtml !== null) priceElement.innerHTML = this.originalPriceHtml;
          return;
        }

        const price = this.formatMoney(prices.price);
        const onSale = prices.compareAtPrice > prices.price;

        priceElement.innerHTML = `
          <div class="f-price f-price--large${onSale ? ' f-price--on-sale' : ''}">
            <div class="f-price__regular f-price--large">
              <span class="f-price-item f-price-item--regular">${price}</span>
            </div>
            <div class="f-price__sale f-price--large">
              <span class="f-price-item f-price-item--sale">${price}</span>
              ${
                onSale
                  ? `<span class="f-price-item f-price-item--regular"><s>${this.formatMoney(prices.compareAtPrice)}</s></span>`
                  : ''
              }
            </div>
          </div>
        `;
      }

      updateSubmitPrice(price) {
        const submitButton = document.getElementById(this.dataset.submitId);
        const submitPrice = submitButton?.querySelector('.product-form__submit-price');
        if (!submitPrice || submitButton.hasAttribute('disabled')) return;

        submitPrice.textContent = `${this.dataset.addToCartText} | ${this.formatMoney(price)}`;
      }
    }
  );
}
